import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import * as S from './Shops.styles';
import {
  buildShopsListQuery,
  mapOrderToSort,
  mapSortToOrder,
  nextShopsListSeed,
  parseShopsFiltersFromQuery,
  type ShopsListFilters,
} from './Shops.helpers';
import { Footer, DesktopFooter } from '@/components/Footer';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ShopsList } from '@/components/Shops/ShopsList';
import { Header } from '@/components/Header';
import { PageTopControls } from '@/components/PageTopControls';
import { Toast } from '@/components/UI/Toast';
import {
  GET_SHOPS,
  type GetShopsData,
  type GetShopsVars,
} from '@/api/queries/shops.queries';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { ShopsEmptySearchResults } from '@/components/Shops/ShopsEmptySearchResults';
import { mergeShopsResults } from '@/utils/helpers';
import { SHOPS_LIST_PAGE_SIZE } from '@/constants/common';
import { ShopsCategoryFilter } from '@/components/Shops/ShopsFilters/ShopsCategoryFilter';
import { ShopsSearch } from '@/components/Shops/ShopsFilters/ShopsSearch';
import { ShopsSortFilter } from '@/components/Shops/ShopsFilters/ShopsSortFilter';
import type { Category } from '@/api/types/categories.types';
import { ShopOrderEnum } from '@/api/types/shop.types';

export const ShopsView: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isDesktop } = useBreakpoint();
  const isBrowser = typeof window !== 'undefined';

  const seedRef = useRef(nextShopsListSeed());
  const [isLoadingMore, setLoadingMore] = useState(false);

  const appliedFilters = useMemo((): ShopsListFilters | null => {
    if (!router.isReady) return null;
    return parseShopsFiltersFromQuery(router.query);
  }, [router.isReady, router.query]);

  const updateShopsFiltersInUrl = useCallback(
    (patch: Partial<ShopsListFilters>, method: 'push' | 'replace'): void => {
      if (!router.isReady || !appliedFilters) return;

      const next: ShopsListFilters = { ...appliedFilters, ...patch };
      const nav = method === 'replace' ? router.replace : router.push;
      nav(
        {
          pathname: router.pathname,
          query: buildShopsListQuery(router.query, next),
        },
        undefined,
        { shallow: true }
      );
    },
    [router, appliedFilters]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed === (appliedFilters?.search.trim() ?? '')) return;

      updateShopsFiltersInUrl({ search: trimmed }, 'replace');
    },
    [updateShopsFiltersInUrl, appliedFilters?.search]
  );

  const handleCategoryChange = useCallback(
    (category: Category | null) => {
      updateShopsFiltersInUrl(
        {
          categoryId: category ? String(category.id) : null,
        },
        'push'
      );
    },
    [updateShopsFiltersInUrl]
  );

  const handleSortChange = useCallback(
    (nextSortValue: string) => {
      const nextOrder = mapSortToOrder(String(nextSortValue));
      const currentOrder = appliedFilters?.order ?? ShopOrderEnum.Default;

      if (
        nextOrder === ShopOrderEnum.Default &&
        currentOrder !== ShopOrderEnum.Default
      ) {
        seedRef.current = nextShopsListSeed();
      }

      updateShopsFiltersInUrl({ order: nextOrder }, 'push');
    },
    [updateShopsFiltersInUrl, appliedFilters?.order]
  );

  const queryVariables = useMemo<GetShopsVars | null>(() => {
    if (!appliedFilters) return null;

    return {
      first: SHOPS_LIST_PAGE_SIZE,
      order: appliedFilters.order,
      search: appliedFilters.search.trim() || null,
      categoriesIds: appliedFilters.categoryId
        ? [appliedFilters.categoryId]
        : null,
      seed: seedRef.current,
    };
  }, [appliedFilters]);

  const { data, loading, fetchMore } = useQuery<GetShopsData, GetShopsVars>(
    GET_SHOPS,
    {
      variables: queryVariables ?? undefined,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !isBrowser || !queryVariables,
    }
  );

  const shopsConnection = data?.shops;

  const shops = shopsConnection?.nodes ?? [];
  const { endCursor, hasNextPage } = shopsConnection?.pageInfo ?? {};

  const listLoading = loading && shops.length === 0;

  const hasActiveShopFilters =
    (appliedFilters?.search.trim() ?? '') !== '' ||
    appliedFilters?.categoryId != null ||
    (appliedFilters?.order ?? ShopOrderEnum.Default) !== ShopOrderEnum.Default;

  const showNoMatchingShops =
    hasActiveShopFilters && !listLoading && shops.length === 0;

  const handleLoadMore = useCallback(() => {
    if (
      listLoading ||
      isLoadingMore ||
      !hasNextPage ||
      !endCursor ||
      !navigator.onLine
    )
      return;

    setLoadingMore(true);
    fetchMore({
      variables: { after: endCursor, seed: seedRef.current },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeShopsResults(prevResult, fetchMoreResult),
    }).finally(() => setLoadingMore(false));
  }, [listLoading, isLoadingMore, hasNextPage, endCursor, fetchMore]);

  const sortValue = mapOrderToSort(
    appliedFilters?.order ?? ShopOrderEnum.Default
  );

  return (
    <>
      <PageTopControls>
        <Header hasBurger />
      </PageTopControls>

      <S.ShopsContentWrap>
        <S.ShopsContentInner>
          <S.PageTitle $variant="1">{t('common.shops')}</S.PageTitle>
          <S.TopFiltersRow>
            <S.FiltersBox>
              {router.isReady && (
                <ShopsSearch
                  defaultSearch={appliedFilters?.search ?? ''}
                  onSearchChange={handleSearchChange}
                />
              )}
              <S.FiltersControls>
                <ShopsCategoryFilter
                  selectedCategoryId={appliedFilters?.categoryId ?? null}
                  onCategoryChange={handleCategoryChange}
                  onInvalidSelectedCategoryId={() =>
                    updateShopsFiltersInUrl({ categoryId: null }, 'replace')
                  }
                />
                <ShopsSortFilter
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                />
              </S.FiltersControls>
            </S.FiltersBox>
          </S.TopFiltersRow>
          {showNoMatchingShops ? (
            <ShopsEmptySearchResults />
          ) : (
            <ShopsList shops={shops} loading={listLoading} />
          )}
        </S.ShopsContentInner>
      </S.ShopsContentWrap>
      {!showNoMatchingShops && (
        <InfiniteLoader onVisit={handleLoadMore} rootMargin={200} />
      )}
      {isDesktop ? <DesktopFooter /> : <Footer />}
      <Toast />
    </>
  );
};
